import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../common/Navbar';
import { 
  Save, 
  X, 
  Building2, 
  User, 
  Phone, 
  Mail, 
  Calendar, 
  MapPin, 
  DollarSign,
  FileText,
  Briefcase,
  TrendingUp,
  AlertCircle
} from 'lucide-react';
import { leadService } from '../../services/api';

export default function LeadForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [lookups, setLookups] = useState({
    consultants: [],
    sectors: [],
    leadSources: [],
    leadStatuses: [],
    countries: []
  });
  
  const [formData, setFormData] = useState({
    date_contacted: new Date().toISOString().split('T')[0],
    consultant_id: '',
    sector_id: '',
    lead_source_id: '',
    lead_status_id: '',
    country_id: '',
    organisation_name: '',
    client_name: '',
    contact_info: '',
    email_address: '',
    service_offered: '',
    estimated_revenue: '',
    actual_revenue: '0',
    next_follow_up_date: '',
    notes: ''
  });

  useEffect(() => {
    loadLookups();
    if (isEdit) {
      loadLead();
    }
  }, [id]);

  const loadLookups = async () => {
    try {
      const response = await leadService.getLookups();
      setLookups(response.data.data);
    } catch (error) {
      console.error('Error loading lookups:', error);
      alert('Failed to load form data. Please refresh the page.');
    }
  };

  const loadLead = async () => {
    try {
      const response = await leadService.getById(id);
      const lead = response.data.data.lead;
      setFormData({
        date_contacted: lead.date_contacted?.split('T')[0] || '',
        consultant_id: lead.consultant_id || '',
        sector_id: lead.sector_id || '',
        lead_source_id: lead.lead_source_id || '',
        lead_status_id: lead.lead_status_id || '',
        country_id: lead.country_id || '',
        organisation_name: lead.organisation_name || '',
        client_name: lead.client_name || '',
        contact_info: lead.contact_info || '',
        email_address: lead.email_address || '',
        service_offered: lead.service_offered || '',
        estimated_revenue: lead.estimated_revenue || '',
        actual_revenue: lead.actual_revenue || '0',
        next_follow_up_date: lead.next_follow_up_date?.split('T')[0] || '',
        notes: lead.notes || ''
      });
    } catch (error) {
      console.error('Error loading lead:', error);
      alert('Failed to load lead details.');
      navigate('/admin/leads');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.organisation_name.trim()) {
      newErrors.organisation_name = 'Organisation name is required';
    }
    if (!formData.client_name.trim()) {
      newErrors.client_name = 'Client name is required';
    }
    if (!formData.date_contacted) {
      newErrors.date_contacted = 'Date contacted is required';
    }
    if (!formData.consultant_id) {
      newErrors.consultant_id = 'Please select a consultant';
    }
    if (!formData.sector_id) {
      newErrors.sector_id = 'Please select a sector';
    }
    if (!formData.lead_source_id) {
      newErrors.lead_source_id = 'Please select a lead source';
    }
    if (!formData.lead_status_id) {
      newErrors.lead_status_id = 'Please select a status';
    }
    if (!formData.country_id) {
      newErrors.country_id = 'Please select a country';
    }
    if (!formData.service_offered.trim()) {
      newErrors.service_offered = 'Service offered is required';
    }

    // Email validation
    if (formData.email_address) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email_address)) {
        newErrors.email_address = 'Invalid email format';
      }
    }

    // Revenue validation
    if (formData.estimated_revenue && isNaN(formData.estimated_revenue)) {
      newErrors.estimated_revenue = 'Must be a valid number';
    }
    if (formData.actual_revenue && isNaN(formData.actual_revenue)) {
      newErrors.actual_revenue = 'Must be a valid number';
    }

    // Date validation
    if (formData.next_follow_up_date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const followUpDate = new Date(formData.next_follow_up_date);
      if (followUpDate < today) {
        newErrors.next_follow_up_date = 'Follow-up date should be in the future';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      alert('Please fix the errors in the form');
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        await leadService.update(id, formData);
        alert('Lead updated successfully');
      } else {
        await leadService.create(formData);
        alert('Lead created successfully');
      }
      navigate('/admin/leads');
    } catch (error) {
      console.error('Error saving lead:', error);
      alert(error.response?.data?.message || 'Failed to save lead. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
      navigate('/admin/leads');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-400 text-lg">Loading lead details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Navbar />
      
      <div className="p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {isEdit ? 'Edit Lead' : 'Create New Lead'}
          </h1>
          <p className="text-slate-400">
            {isEdit ? 'Update lead information below' : 'Enter new lead information below'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Organisation Information Section */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">Organisation Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Organisation Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Organisation Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="organisation_name"
                  value={formData.organisation_name}
                  onChange={handleChange}
                  placeholder="e.g., Acme Corporation"
                  className={`w-full px-4 py-3 bg-slate-800 border ${
                    errors.organisation_name ? 'border-red-500' : 'border-slate-700'
                  } rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all`}
                />
                {errors.organisation_name && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.organisation_name}
                  </p>
                )}
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Country <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <select
                    name="country_id"
                    value={formData.country_id}
                    onChange={handleChange}
                    className={`w-full pl-11 pr-4 py-3 bg-slate-800 border ${
                      errors.country_id ? 'border-red-500' : 'border-slate-700'
                    } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all appearance-none cursor-pointer`}
                  >
                    <option value="">Select Country</option>
                    {lookups.countries.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                {errors.country_id && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.country_id}
                  </p>
                )}
              </div>

              {/* Sector */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Sector <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <select
                    name="sector_id"
                    value={formData.sector_id}
                    onChange={handleChange}
                    className={`w-full pl-11 pr-4 py-3 bg-slate-800 border ${
                      errors.sector_id ? 'border-red-500' : 'border-slate-700'
                    } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all appearance-none cursor-pointer`}
                  >
                    <option value="">Select Sector</option>
                    {lookups.sectors.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                {errors.sector_id && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.sector_id}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">Contact Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Client Name */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Client Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleChange}
                  placeholder="e.g., John Smith"
                  className={`w-full px-4 py-3 bg-slate-800 border ${
                    errors.client_name ? 'border-red-500' : 'border-slate-700'
                  } rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all`}
                />
                {errors.client_name && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.client_name}
                  </p>
                )}
              </div>

              {/* Contact Info */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Contact Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="text"
                    name="contact_info"
                    value={formData.contact_info}
                    onChange={handleChange}
                    placeholder="e.g., +27 11 123 4567"
                    className="w-full pl-11 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="email"
                    name="email_address"
                    value={formData.email_address}
                    onChange={handleChange}
                    placeholder="e.g., john.smith@acme.com"
                    className={`w-full pl-11 pr-4 py-3 bg-slate-800 border ${
                      errors.email_address ? 'border-red-500' : 'border-slate-700'
                    } rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all`}
                  />
                </div>
                {errors.email_address && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.email_address}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Lead Details Section */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-600 to-orange-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">Lead Details</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Date Contacted */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Date Contacted <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="date"
                    name="date_contacted"
                    value={formData.date_contacted}
                    onChange={handleChange}
                    className={`w-full pl-11 pr-4 py-3 bg-slate-800 border ${
                      errors.date_contacted ? 'border-red-500' : 'border-slate-700'
                    } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all`}
                  />
                </div>
                {errors.date_contacted && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.date_contacted}
                  </p>
                )}
              </div>

              {/* Next Follow Up */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Next Follow Up Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="date"
                    name="next_follow_up_date"
                    value={formData.next_follow_up_date}
                    onChange={handleChange}
                    className={`w-full pl-11 pr-4 py-3 bg-slate-800 border ${
                      errors.next_follow_up_date ? 'border-red-500' : 'border-slate-700'
                    } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all`}
                  />
                </div>
                {errors.next_follow_up_date && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.next_follow_up_date}
                  </p>
                )}
              </div>

              {/* Consultant */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Consultant <span className="text-red-400">*</span>
                </label>
                <select
                  name="consultant_id"
                  value={formData.consultant_id}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-slate-800 border ${
                    errors.consultant_id ? 'border-red-500' : 'border-slate-700'
                  } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all appearance-none cursor-pointer`}
                >
                  <option value="">Select Consultant</option>
                  {lookups.consultants.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {errors.consultant_id && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.consultant_id}
                  </p>
                )}
              </div>

              {/* Lead Source */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Lead Source <span className="text-red-400">*</span>
                </label>
                <select
                  name="lead_source_id"
                  value={formData.lead_source_id}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-slate-800 border ${
                    errors.lead_source_id ? 'border-red-500' : 'border-slate-700'
                  } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all appearance-none cursor-pointer`}
                >
                  <option value="">Select Source</option>
                  {lookups.leadSources.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {errors.lead_source_id && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.lead_source_id}
                  </p>
                )}
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Status <span className="text-red-400">*</span>
                </label>
                <select
                  name="lead_status_id"
                  value={formData.lead_status_id}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-slate-800 border ${
                    errors.lead_status_id ? 'border-red-500' : 'border-slate-700'
                  } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all appearance-none cursor-pointer`}
                >
                  <option value="">Select Status</option>
                  {lookups.leadStatuses.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {errors.lead_status_id && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.lead_status_id}
                  </p>
                )}
              </div>

              {/* Estimated Revenue */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Estimated Revenue (ZAR)
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="number"
                    name="estimated_revenue"
                    value={formData.estimated_revenue}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className={`w-full pl-11 pr-4 py-3 bg-slate-800 border ${
                      errors.estimated_revenue ? 'border-red-500' : 'border-slate-700'
                    } rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all`}
                  />
                </div>
                {errors.estimated_revenue && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.estimated_revenue}
                  </p>
                )}
              </div>

              {/* Actual Revenue */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Actual Revenue (ZAR)
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="number"
                    name="actual_revenue"
                    value={formData.actual_revenue}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className={`w-full pl-11 pr-4 py-3 bg-slate-800 border ${
                      errors.actual_revenue ? 'border-red-500' : 'border-slate-700'
                    } rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all`}
                  />
                </div>
                {errors.actual_revenue && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.actual_revenue}
                  </p>
                )}
              </div>

              {/* Service Offered */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Service Offered <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
                  <textarea
                    name="service_offered"
                    value={formData.service_offered}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Describe the service or solution being offered..."
                    className={`w-full pl-11 pr-4 py-3 bg-slate-800 border ${
                      errors.service_offered ? 'border-red-500' : 'border-slate-700'
                    } rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all resize-none`}
                  />
                </div>
                {errors.service_offered && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.service_offered}
                  </p>
                )}
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Additional Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Add any additional notes, comments, or important information..."
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all resize-none"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 sticky bottom-8 bg-slate-950/95 backdrop-blur-sm rounded-2xl p-4 border border-slate-800">
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              className="flex-1 px-6 py-4 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="w-5 h-5" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-amber-500/20"
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {isEdit ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {isEdit ? 'Update Lead' : 'Create Lead'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}